import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { Geofence } from '@/services/db/geofence/entity';
import { useRequiredGeosetIdFromCurrentUrl } from '@/services/router/use-geoset-id';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import {
  geosetKeys,
  parseGeoset,
  parseGeosetFence,
  type Geoset,
  type GeosetFence,
  type GeosetListParams,
} from './entity';
import { geosetFenceToGeofence } from './fence-adapter';
import { parseGeosetsListResponse, populateGeosetsList, type ParsedGeosetsListResponse } from './populate';

const STALE_TIME = 60_000;

// getFences is paged (max 1000/page). A geoset can hold up to the 10k
// per-geoset cap, so we page through the whole membership — the geoset detail
// page renders table + map client-side over the full set. The max-pages guard
// (10k / 1000 + headroom) stops a runaway loop if meta.total is ever wrong.
const GEOSET_FENCES_PAGE_SIZE = 1000;
const GEOSET_FENCES_MAX_PAGES = 12;

/** The list endpoint is unparameterized, so every list read shares one slot. */
const LIST_PARAMS: GeosetListParams = {};

function useValidWorkspaceId(): number | null {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  return workspaceId != null && workspaceId > 0 ? workspaceId : null;
}

/* ------------------------------------------------------------------------- */
/* listGeosets — the workspace's full geoset collection                       */
/* ------------------------------------------------------------------------- */

/** @public */
export async function listGeosets(workspaceId: number, qc: QueryClient): Promise<ParsedGeosetsListResponse> {
  const raw = await hydraUiApi.geosets.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const response = parseGeosetsListResponse(raw);
  populateGeosetsList(qc, workspaceId, response);
  return response;
}

export const listGeosetsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: geosetKeys.list(workspaceId, LIST_PARAMS),
    queryFn: () => listGeosets(workspaceId, qc),
    staleTime: STALE_TIME,
  });

interface UseGeosetsOptions {
  queryConfig?: Omit<
    UseQueryOptions<ParsedGeosetsListResponse, Error, ParsedGeosetsListResponse, readonly unknown[]>,
    'queryKey' | 'queryFn'
  >;
}

/**
 * Non-suspending read of the workspace's geosets for the geosets table.
 * Returns `data: undefined` until a workspace id resolves from the URL.
 */
/** @public */
export const useGeosets = ({ queryConfig }: UseGeosetsOptions = {}) => {
  const workspaceId = useValidWorkspaceId();
  const qc = useQueryClient();

  return useQuery<ParsedGeosetsListResponse, Error, ParsedGeosetsListResponse, readonly unknown[]>({
    queryKey: workspaceId != null ? geosetKeys.list(workspaceId, LIST_PARAMS) : ['geoset', 'no-workspace'],
    queryFn: workspaceId != null ? () => listGeosets(workspaceId, qc) : skipToken,
    staleTime: STALE_TIME,
    ...queryConfig,
  });
};

/**
 * Cheap workspace geoset count; `undefined` while loading or on error.
 * The list endpoint returns the whole collection, so the count is just the
 * length of that same cached read — no extra request. If a dedicated count
 * endpoint ever lands, swap the internals here; callers only see the number.
 *
 * @public
 */
export const useGeosetCount = (): number | undefined => {
  const workspaceId = useValidWorkspaceId();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: workspaceId != null ? geosetKeys.list(workspaceId, LIST_PARAMS) : ['geoset', 'no-workspace'],
    queryFn: workspaceId != null ? () => listGeosets(workspaceId, qc) : skipToken,
    staleTime: STALE_TIME,
  });
  return data?.geosets.length;
};

/* ------------------------------------------------------------------------- */
/* getGeoset — by-id detail                                                   */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getGeoset(workspaceId: number, geosetId: string, _qc: QueryClient): Promise<Geoset> {
  const data = await hydraUiApi.geosets.get({
    params: { path: { environment_id: String(workspaceId), id: geosetId } },
  });
  if (data.geoset == null) {
    throw new Error('missing geoset in get geoset response');
  }
  return parseGeoset(data.geoset);
}

/** @public */
export const getGeosetQueryOptions = (workspaceId: number, geosetId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: geosetKeys.detail(workspaceId, geosetId),
    queryFn: () => getGeoset(workspaceId, geosetId, qc),
    staleTime: STALE_TIME,
  });

/* ------------------------------------------------------------------------- */
/* listGeosetFences — the fences that belong to one geoset                    */
/* ------------------------------------------------------------------------- */

/**
 * Query key for a geoset's fences. Exposed so callers that mutate the
 * underlying geofences (e.g. delete) can invalidate this list — the geofence
 * mutations only touch the workspace geofence keys, not this geoset-scoped one.
 *
 * @public
 */
export function geosetFencesQueryKey(workspaceId: number, geosetId: string) {
  return [...geosetKeys.detail(workspaceId, geosetId), 'fences'] as const;
}

/** @public */
export async function listGeosetFences(workspaceId: number, geosetId: string): Promise<GeosetFence[]> {
  const fences: GeosetFence[] = [];
  for (let page = 1; page <= GEOSET_FENCES_MAX_PAGES; page += 1) {
    const raw = await hydraUiApi.geosets.getFences({
      params: {
        path: { environment_id: String(workspaceId), id: geosetId },
        query: { page, page_size: GEOSET_FENCES_PAGE_SIZE },
      },
    });
    const batch = raw.fences ?? [];
    for (const fence of batch) fences.push(parseGeosetFence(fence));
    // An empty page always means we're done. Otherwise, prefer meta.total when
    // the response provides it (stop once we've read the whole membership); if
    // it's missing, fall back to the page size — a short page is the last one.
    // The missing-total fallback keeps us paging instead of truncating after
    // page 1. The max-pages loop bound is the final backstop.
    if (batch.length === 0) break;
    const total = raw.meta?.total;
    const reachedEnd = total != null ? fences.length >= total : batch.length < GEOSET_FENCES_PAGE_SIZE;
    if (reachedEnd) break;
  }
  return fences;
}

/**
 * The fences in a geoset. `null` geosetId (or no workspace) skips the fetch,
 * so callers can pass the id straight from the URL without a guard.
 *
 * @public
 */
export const useGeosetFences = (geosetId: string | null) => {
  const workspaceId = useValidWorkspaceId();
  const enabled = workspaceId != null && geosetId != null;

  return useQuery({
    queryKey: enabled ? geosetFencesQueryKey(workspaceId, geosetId) : ['geoset', 'fences', 'no-workspace'],
    queryFn: enabled ? () => listGeosetFences(workspaceId, geosetId) : skipToken,
    staleTime: STALE_TIME,
  });
};

interface UseGeosetOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getGeosetQueryOptions>;
}

/** @public */
export const useGeoset = ({ id, queryConfig }: UseGeosetOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getGeosetQueryOptions(workspaceId, id, qc),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* getGeosetFence — a single fence in a geoset, by id                         */
/* ------------------------------------------------------------------------- */

/**
 * Query key for a single geoset fence. Nested under the geoset's fences list
 * key so invalidating the list (after a create/update/delete) also drops the
 * cached detail.
 *
 * @public
 */
export function geosetFenceDetailKey(workspaceId: number, geosetId: string, fenceId: string) {
  return [...geosetFencesQueryKey(workspaceId, geosetId), fenceId] as const;
}

/**
 * Read a single fence in a geoset, adapted to the workspace `Geofence` shape
 * the geofences UI renders (see `geosetFenceToGeofence`).
 *
 * @public
 */
export async function getGeosetFence(workspaceId: number, geosetId: string, fenceId: string): Promise<Geofence> {
  const data = await hydraUiApi.geosets.getFencesByFence({
    params: { path: { environment_id: String(workspaceId), id: geosetId, fence_id: fenceId } },
  });
  if (data.fence == null) {
    throw new Error('missing fence in get geoset fence response');
  }
  return geosetFenceToGeofence(parseGeosetFence(data.fence));
}

/** @public */
export const getGeosetFenceQueryOptions = (workspaceId: number, geosetId: string, fenceId: string) =>
  queryOptions({
    queryKey: geosetFenceDetailKey(workspaceId, geosetId, fenceId),
    queryFn: () => getGeosetFence(workspaceId, geosetId, fenceId),
    staleTime: STALE_TIME,
  });

interface UseGeosetFenceOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getGeosetFenceQueryOptions>;
}

/**
 * Suspense read of a single fence in the geoset from the current URL. Mirrors
 * the shape of the retired `useGeofence` so the edit page consumes it the same
 * way — the geoset scope comes from the route.
 *
 * @public
 */
export const useGeosetFence = ({ id, queryConfig }: UseGeosetFenceOptions) => {
  const workspaceId = requireWorkspaceId();
  const geosetId = useRequiredGeosetIdFromCurrentUrl();
  return useSuspenseQuery({
    ...getGeosetFenceQueryOptions(workspaceId, geosetId, id),
    ...queryConfig,
  });
};
